import { createAction } from '@reduxjs/toolkit';

import type { ProfileSelection } from '../domain/edit-draft.model';

/**
 * "Signal" actions: intents the UI dispatches for the saga to act on. They are
 * deliberately kept out of the slice's reducers: they trigger side effects, not
 * state transitions (the saga owns the resulting `load*` / `save*` / `delete*`
 * transitions).
 */

/** Load the list to edit by id (dispatched on mount). */
export const loadRequested = createAction<string>('editList/loadRequested');

/** Start the full multi-phase save flow with the UI-computed selection. */
export const submitRequested = createAction<ProfileSelection>('editList/submitRequested');

/** Abort an in-flight save (also emitted implicitly by re-submitting). */
export const submitCancelled = createAction('editList/submitCancelled');

/** Delete the list currently being edited. */
export const deleteRequested = createAction('editList/deleteRequested');

/** Compute the enrichment buckets (dispatched when entering the confirm step). */
export const bucketsRequested = createAction('editList/bucketsRequested');

/** Analyse the scope text into a filter tree (dispatched when entering select). */
export const scopeAnalyzeRequested = createAction('editList/scopeAnalyzeRequested');

/**
 * Parse the file dropped on the embedded import step. The `File` payload is
 * deliberately non-serialisable (listed in the store's
 * `serializableCheck.ignoredActions`); the saga consumes it for the upload
 * call and only its name enters the state.
 */
export const fileParseRequested = createAction<File>('editList/fileParseRequested');

/** Dry-run the mapped rows of the embedded import step. */
export const fileAnalyzeRequested = createAction('editList/fileAnalyzeRequested');

/** Load the licence dropdown of the import step. */
export const licencesRequested = createAction('editList/licencesRequested');

/** Load the source-list dropdown of the import step. */
export const sourceListsRequested = createAction('editList/sourceListsRequested');

/** Import every profile of the chosen source list (then check duplicates). */
export const listImportRequested = createAction<{ listId: string; listName: string }>(
  'editList/listImportRequested',
);

/** (Re)fetch the imported-profiles preview page (ids/page/pageSize live in state). */
export const importPreviewRequested = createAction('editList/importPreviewRequested');
